import React from "react";
import { Button } from "../button/button";
import { navigate } from "gatsby";
import {
  BlankSection,
  BlankSectionNotSpacing,
  SectionWithHeading,
} from "../layouts/section";
import textToUrlPath from "../../utils/textToUrlPath";
import GridWithText from "../features/gridWithText";
import Image from "../../images/image";
import { getImageAltText } from "../../utils/manipulation";
import SimpleDivider from "../dividers/simpleDivider";

function SimpleCentered({
  title,
  subtitle,
  shortdescription,
  primaryCtaLink,
  primaryCtaText,
  secondaryCtaText,
  secondaryCtaLink,
  anchorTag,
  bgClass,
  video,
  videoButtonText,
  isNotHero,
  category,
}) {
  return (
    <BlankSection bgClass={bgClass} anchorTag={anchorTag}>
      <div className="text-center">
        {category && (
          <div className="text-lg font-medium text-primary-500 mb-3 mx-auto uppercase">
            {category}
          </div>
        )}
        {subtitle ? (
          <h1 className="text-base text-primary-600 uppercase mb-1">
            {subtitle}
          </h1>
        ) : null}
        {/* If the component is not hero the font size is smaller */}
        {isNotHero ? (
          <h1 className="text-3xl tracking-tight font-bold text-gray-900 sm:text-4xl">
            <span className="block xl:inline">{title}</span>
          </h1>
        ) : (
          <h1 className="text-4xl tracking-tight font-bold text-gray-900 sm:text-5xl md:text-6xl">
            <span className="block xl:inline">{title}</span>
          </h1>
        )}

        {shortdescription && (
          <p
            className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl"
            dangerouslySetInnerHTML={{
              __html: shortdescription,
            }}
          />
        )}
        <div className="mt-5 max-w-md mx-auto sm:flex sm:justify-center md:mt-8">
          {primaryCtaText && (
            <Button.BaseButton
              buttonStyle="fill"
              size="2xl"
              className="w-full lg:w-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-500"
              video={video}
              onClickLink={primaryCtaLink}
              // onClick={() => {
              //   video ? openVideoModal() : navigate(primaryCtaLink);
              // }}
            >
              {primaryCtaText}
            </Button.BaseButton>
          )}
          {secondaryCtaText && (
            <Button.BaseButton
              buttonStyle="outline"
              size="2xl"
              className="w-full lg:w-auto mt-3 sm:mt-0 sm:ml-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-500"
              video={video}
              onClickLink={secondaryCtaLink}
              // onClick={() => {
              //   video ? openVideoModal() : navigate(secondaryCtaLink);
              // }}
            >
              {secondaryCtaText}
            </Button.BaseButton>
          )}
        </div>
      </div>
    </BlankSection>
  );
}

function SimpleCenteredWithChildrenNoSpacing({
  title,
  subtitle,
  shortdescription,
  primaryCtaLink,
  primaryCtaText,
  secondaryCtaLink,
  secondaryCtaText,
  anchorTag,
  bgClass,
  isNotHero,
  children,
}) {
  return (
    <BlankSectionNotSpacing bgClass={bgClass} anchorTag={anchorTag}>
      <div className="text-center">
        {subtitle ? (
          <h1 className="text-base text-primary-600 uppercase mb-1">
            {subtitle}
          </h1>
        ) : null}
        {/* If the component is not hero the font size is smaller */}
        {isNotHero ? (
          <h1 className="text-3xl tracking-tight font-bold text-gray-900 sm:text-4xl">
            <span className="block xl:inline">{title}</span>
          </h1>
        ) : (
          <h1 className="text-4xl tracking-tight font-bold text-gray-900 sm:text-5xl md:text-6xl">
            <span className="block xl:inline">{title}</span>
          </h1>
        )}

        {shortdescription && (
          <p
            className="mt-3 max-w-md mx-auto text-base text-gray-500 text-lg md:mt-5 md:text-xl md:max-w-3xl"
            dangerouslySetInnerHTML={{
              __html: shortdescription,
            }}
          />
        )}
        <div className="my-16">{children}</div>
        <div className="mt-5 max-w-md mx-auto sm:flex sm:justify-center md:mt-8">
          {primaryCtaLink && (
            <Button.BaseButton
              buttonStyle="fill"
              size="2xl"
              className="w-full lg:w-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-500"
              onClick={() => {
                navigate(primaryCtaLink);
              }}
            >
              {primaryCtaText}
            </Button.BaseButton>
          )}
          {secondaryCtaLink && (
            <Button.BaseButton
              buttonStyle="outline"
              size="2xl"
              className="w-full lg:w-auto mt-3 sm:mt-0 sm:ml-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-500"
              onClick={() => {
                navigate(secondaryCtaLink);
              }}
            >
              {secondaryCtaText}
            </Button.BaseButton>
          )}
        </div>
      </div>
    </BlankSectionNotSpacing>
  );
}

function SimpleCenteredBrand({
  heading,
  description,
  primaryCtaText,
  primaryCtaLink,
  secondaryCtaText,
  secondaryCtaLink,
  anchorTag,
  bgClass,
}) {
  anchorTag = anchorTag || textToUrlPath(heading);
  return (
    <section
      className={`bg-primary-700 relative overflow-hidden ${
        bgClass ? bgClass : ""
      }`}
      id={anchorTag}
    >
      <div className="h-full w-full absolute overlay-top bg-primary-800 opacity-40"></div>
      <div className="h-full w-full absolute overlay-bottom bg-primary-600 opacity-40"></div>
      <div className="max-w-7xl mx-auto relative text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-white sm:text-4xl">{heading}</h2>
        <p className="mt-4 text-lg font-normal leading-6 text-white sm:text-xl">
          {description}
        </p>
        <div className="mt-8 md:flex md:justify-center">
          <div className="md:inline-flex">
            <Button.BaseButton
              buttonStyle="outline"
              size="xl"
              className="w-full lg:w-auto"
              onClick={() => {
                navigate(primaryCtaLink);
              }}
            >
              <span className="font-medium">{primaryCtaText}</span>
            </Button.BaseButton>
          </div>
          {secondaryCtaLink && (
            <div className="mt-4 md:mt-0 md:ml-3 md:inline-flex">
              <Button.BaseButton
                buttonStyle="negative"
                size="xl"
                className="w-full lg:w-auto"
                onClick={() => {
                  navigate(secondaryCtaLink);
                }}
              >
                <span className="font-medium">{secondaryCtaText}</span>
              </Button.BaseButton>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function SimpleCenteredWithImageBelow({
  title,
  subtitle,
  shortdescription,
  primaryCtaLink,
  primaryCtaText,
  secondaryCtaText,
  secondaryCtaLink,
  anchorTag,
  bgClass,
  video,
  videoButtonText,
  isNotHero,
  category,
  aboutContent,
  content,
  heading,
  image,
}) {
  return (
    <>
      <div className="">
        <BlankSection bgClass={bgClass} anchorTag={anchorTag}>
          <div className="text-center">
            {category && (
              <div className="text-lg font-medium text-primary-500 mb-3 mx-auto uppercase">
                {category}
              </div>
            )}
            {subtitle ? (
              <h1 className="text-base text-primary-600 uppercase mb-1">
                {subtitle}
              </h1>
            ) : null}
            {/* If the component is not hero the font size is smaller */}
            {isNotHero ? (
              <h1 className="text-3xl tracking-tight font-bold text-gray-900 sm:text-4xl">
                <span className="block xl:inline">{title}</span>
              </h1>
            ) : (
              <h1 className="text-4xl tracking-tight font-bold text-gray-900 sm:text-5xl md:text-6xl">
                <span className="block xl:inline">{title}</span>
              </h1>
            )}

            {shortdescription && (
              <p
                className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl"
                dangerouslySetInnerHTML={{
                  __html: shortdescription,
                }}
              />
            )}
            <div className="mt-5 max-w-md mx-auto sm:flex sm:justify-center md:mt-8">
              {primaryCtaText && (
                <Button.BaseButton
                  buttonStyle="fill"
                  size="2xl"
                  className="w-full lg:w-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-500"
                  video={video}
                  onClickLink={primaryCtaLink}
                  // onClick={() => {
                  //   video ? openVideoModal() : navigate(primaryCtaLink);
                  // }}
                >
                  {primaryCtaText}
                </Button.BaseButton>
              )}
              {secondaryCtaText && (
                <Button.BaseButton
                  buttonStyle="outline"
                  size="2xl"
                  className="w-full lg:w-auto mt-3 sm:mt-0 sm:ml-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-500"
                  video={video}
                  onClickLink={secondaryCtaLink}
                  // onClick={() => {
                  //   video ? openVideoModal() : navigate(secondaryCtaLink);
                  // }}
                >
                  {secondaryCtaText}
                </Button.BaseButton>
              )}
            </div>
          </div>
        </BlankSection>
        <div className="relative">
          <div className="relative">
            <div className="absolute inset-0 flex flex-col" aria-hidden="true">
              <div className={`flex-1 ${bgClass ? bgClass : ""}`} />
              <div className={`flex-1 w-full`} />
            </div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <Image
                image={image}
                alt={getImageAltText(image)}
                className="relative rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
        <div className="mt-5">
          {aboutContent && (
            <SimpleCentered
              shortdescription={aboutContent.childMarkdownRemark.html}
              bgClass="py-8 sm:py-0"
            />
          )}
          <SimpleDivider />
          <div className="max-w-7xl mx-auto py-16 sm:py-24">
            <SectionWithHeading heading={heading} bgClass="">
              <GridWithText listitems={content} className="mt-16" />
            </SectionWithHeading>
          </div>
        </div>
      </div>
    </>
  );
}

function SimpleCenteredCTA({
  heading,
  description,
  PrimaryCtaText,
  PrimaryCtaLink,
  anchorTag,
  bgClass,
  download,
}) {
  anchorTag = anchorTag || textToUrlPath(heading);

  const handlePrimaryCtaLink = (link) => {
    download ? handleDownload(link) : navigate(link);
  };

  /**
   *
   * @param {*} fileUrl
   *
   */
  const handleDownload = (fileUrl) => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.setAttribute("download", "");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <section
      className={`relative overflow-hidden ${bgClass ? bgClass : ""}`}
      id={anchorTag}
    >
      <div className="max-w-7xl mx-auto relative text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold sm:text-4xl">{heading}</h2>
        <p
          className="mt-4 text-lg font-normal leading-6 sm:text-xl"
          dangerouslySetInnerHTML={{
            __html: description.description.html,
          }}
        />
        <div className="mt-8 md:flex md:justify-center">
          <div className="md:inline-flex">
            <Button.BaseButton
              buttonStyle="fill"
              size="xl"
              className="w-full lg:w-auto"
              onClick={() => {
                handlePrimaryCtaLink(PrimaryCtaLink);
              }}
            >
              <span className="font-medium">{PrimaryCtaText}</span>
            </Button.BaseButton>
          </div>
        </div>
      </div>
    </section>
  );
}

export {
  SimpleCentered,
  SimpleCenteredBrand,
  SimpleCenteredWithChildrenNoSpacing,
  SimpleCenteredWithImageBelow,
  SimpleCenteredCTA,
};
